import type { CashAuditModel } from '@/api/AuditController';
import { Denomination, isGoldCode } from '@/constatns/denominations';
import { hasAnyRole } from '@/core/helpers/PageHelpers';
import type { RolesData } from '@/interface';

/**
 * Проводить ревизию — принимать, отклонять, заводить акты, вносить пересчёт, комиссию,
 * причины и завершать — могут только главный кассир и менеджер, и только в отделениях
 * своего города. Остальные роли из AUDIT_ROLES ревизию только просматривают
 */
export const AUDIT_EDIT_ROLES: (keyof RolesData)[] = ["mainCashierCity", "managerCity"];

/** Город роли совпадает с городом отделения — пока отделение не известно, прав нет */
export const canManageAudit = (rolesData: RolesData | undefined, departmentCity?: string | null) => (
    !!departmentCity
    && hasAnyRole(rolesData, AUDIT_EDIT_ROLES)
    && (rolesData?.mainCashierCity === departmentCity || rolesData?.managerCity === departmentCity)
);

/**
 * Название отделения с кодом. Названия у разных точек бывают одинаковыми, а бэк в ревизии
 * отдаёт только depName — код берём из списка отделений по depId. Нет его там (чужая
 * или ещё не загруженная точка) — показываем одно название.
 */
export const auditDepartmentLabel = (
    audit: Pick<CashAuditModel, "depId" | "depName">,
    departments: { id: number; code?: string }[],
) => {
    const name = audit?.depName || `отделение ${audit?.depId}`;
    const code = departments?.find((dep) => dep?.id === audit?.depId)?.code;

    return code ? `${name} - ${code}` : name;
};

/**
 * Ключ количества — сам номинал строкой: ровно в таком виде его принимает бэк
 * (counts: { "KZT": { "10000": 15 } }). Делить банкноты и монеты на разные ключи нельзя —
 * на сервере это один и тот же номинал.
 */
export const denominationKey = (denomination: Denomination) => String(denomination?.nominal);

/** Считаем в копейках, чтобы 3 × 0.1 не превращалось в 0.30000000000000004 */
export const roundMoney = (value: number) => Math.round((+value + Number.EPSILON) * 100) / 100;

/**
 * Итог по номиналам. Сервер считает свой, но ждать ответа на каждый ввод незачем —
 * это то же число для мгновенной подсказки на экране.
 */
export const calcDenominationsSum = (denominations: Denomination[], quantities: Record<string, number>) => (
    roundMoney(
        denominations.reduce((sum, denomination) => (
            sum + Math.round(denomination?.nominal * 100) * (quantities?.[denominationKey(denomination)] || 0)
        ), 0) / 100
    )
);

/**
 * Годные и ветхие ревизор считает разными пачками, но лежат они в одной кассе: в остаток
 * по номиналу входят обе. Складываем количества, а не суммы, — суммы считает сервер.
 * Именно эта сумма и уходит на бэк как общее количество по номиналу.
 */
export const totalQuantities = (
    quantities: Record<string, number>,
    worn: Record<string, number>,
): Record<string, number> => (
    [...Object.keys(quantities || {}), ...Object.keys(worn || {})]
        .reduce<Record<string, number>>((total, key) => {
            total[key] = (quantities?.[key] || 0) + (worn?.[key] || 0);

            return total;
        }, {})
);

/**
 * Обратное к totalQuantities: бэк хранит общее количество и сколько из него ветхих,
 * а ревизор вводит годные отдельно. Ветхих больше общего быть не должно, но если такое
 * пришло, в минус не уходим — поле количества отрицательным не бывает.
 */
export const goodQuantities = (
    total: Record<string, number>,
    worn: Record<string, number>,
): Record<string, number> => (
    Object.keys(total || {}).reduce<Record<string, number>>((good, key) => {
        good[key] = Math.max(0, (total?.[key] || 0) - (worn?.[key] || 0));

        return good;
    }, {})
);

/** Сколько всего купюр набрано по позиции — считаем только по показанным номиналам */
export const calcDenominationsQuantity = (denominations: Denomination[], quantities: Record<string, number>) => (
    (denominations || []).reduce((sum, denomination) => sum + (quantities?.[denominationKey(denomination)] || 0), 0)
);

/** 1 234 567,89 — дробную часть показываем только если она есть */
export const formatAmount = (value: number) => {
    const rounded = roundMoney(+value || 0);
    const [integer, fraction] = Math.abs(rounded).toFixed(2).split(".");
    const spaced = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    return `${rounded < 0 ? "-" : ""}${spaced}${fraction === "00" ? "" : `,${fraction}`}`;
};

/** GOLD5 → «Золото 5 гр», обычная валюта — как есть */
export const currencyTitle = (currencyCode: string) => (
    isGoldCode(currencyCode)
        ? `Золото ${(currencyCode || "").replace(/GOLD/gi, "")} гр`
        : currencyCode
);

/**
 * Тип расхождения задаёт знак. Сервер присылает готовое deviationTypeName, но пока пересчёт
 * не сохранён, отклонение считается на фронте — название собираем сами.
 */
export const deviationName = (deviation: number) => {
    if (!deviation) return "Нет расхождений";

    return deviation > 0 ? "Излишек" : "Недостача";
};

/**
 * Номинал показываем как есть, без округления до копеек: 0,005 у бахрейнского динара —
 * такой же номинал справочника, как 20 000 у тенге.
 */
export const formatDenomination = (value: number) => {
    const [integer, fraction] = String(+value || 0).split(".");
    const spaced = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    return fraction ? `${spaced},${fraction}` : spaced;
};

/** Мельче единицы — разменная монета: в кассе её не пересчитывают, и в акте таких строк нет */
export const MIN_NOMINAL = 1;

/**
 * Номинал из поля ввода: только целое число не меньше единицы, без нулей в начале.
 * Дробные номиналы в акте не считают, поэтому и запятую с точкой не разбираем —
 * это опечатка. null — введено не то.
 */
export const parseDenomination = (text: string): number | null => {
    const normalized = (text || "").trim();

    if (!/^[1-9]\d*$/.test(normalized)) {
        return null;
    }

    return +normalized >= MIN_NOMINAL ? +normalized : null;
};

/** Значащая часть номинала, к которой дописываются нули: 1, 2, 5 и четвертные 25 */
const NOMINAL_BASES = ["1", "2", "5", "25"];

/** Купюры вне общего ряда, которые реально ходят: 3 сомони у таджикского TJS */
const NOMINAL_EXCEPTIONS = [3];

/**
 * Бывает ли такой номинал вообще: 1, 2, 5, 25 с любым числом нулей следом — 10, 200, 250,
 * 5 000, 1 000 000. Это защита от опечатки при добавлении номинала в акте: 77 или 88
 * завелись бы в справочник бэка навсегда. Номиналы, уже сохранённые в актах, правило
 * не трогает — оно только про ввод нового.
 */
export const isPossibleNominal = (nominal: number): boolean => {
    if (!Number.isInteger(nominal) || nominal < MIN_NOMINAL) {
        return false;
    }

    return NOMINAL_EXCEPTIONS.includes(nominal) || NOMINAL_BASES.includes(String(nominal).replace(/0+$/, ""));
};

/**
 * Какие номиналы показать в позиции акта: ходовые купюры валюты, плюс добавленные ревизором,
 * плюс те, по которым в акте уже есть количество, — иначе сохранённый пересчёт пропал бы
 * с экрана. Убранные ревизором строки не показываем: с бэка номинал при этом не удаляется,
 * он просто не участвует в этом акте.
 */
export const buildRowNominals = (source: {
    defaults: number[],
    extra: number[],
    saved: number[],
    hidden: number[],
}): number[] => {
    const hidden = new Set(source?.hidden || []);

    return [...new Set([
        ...(source?.defaults || []),
        ...(source?.extra || []),
        ...(source?.saved || []),
    ])]
        .filter((nominal) => nominal >= MIN_NOMINAL && !hidden.has(nominal))
        .sort((a, b) => b - a);
};
